/**
 * @ims/engine — Config Adapter
 *
 * Maps a ResolvedConfig (from @ims/shared) to an EngineConfig.
 * Bridges the config system with the engine's rendering API.
 *
 * Usage:
 *   const resolved = await resolveConfig('audi', 'q3sportback', 'de');
 *   const config = resolvedConfigToEngineConfig(resolved, container);
 *   const engine = await createEngine(config);
 *
 * @see docs/ims-brand-frontends/02-config-system.md — Resolution-Algorithmus
 */

import type { ResolvedConfig, ModelSceneConfig } from '@ims/shared';
import type { Engine, EngineConfig, LoadAssetConfig, BaseNodeConfig } from './types';

/**
 * Convert a fully resolved config into an EngineConfig.
 * All required engine settings are derived from the resolved config.
 *
 * @param resolved - The output of resolveConfig()
 * @param container - The DOM element to mount the canvas into
 * @param overrides - Optional partial EngineConfig to override derived values
 */
export function resolvedConfigToEngineConfig(
  resolved: ResolvedConfig,
  container: HTMLElement,
  overrides?: Partial<Omit<EngineConfig, 'container' | 'modelUrl'>>,
): EngineConfig {
  const { model, glbUrl } = resolved;

  // Map model camera config to engine camera config
  const camera = model.camera
    ? {
        fov: model.camera.fov,
        minPolarAngle: model.camera.minPolarAngle,
        maxPolarAngle: model.camera.maxPolarAngle,
        minDistance: model.camera.minDistance,
        maxDistance: model.camera.maxDistance,
        target: model.camera.target,
      }
    : undefined;

  // Map vehicle dimensions (m) from model config → engine dimensions config (mm)
  const dimensions = model.dimensions
    ? {
        lengthMm:
          model.dimensions.length !== undefined ? model.dimensions.length * 1000 : undefined,
        widthMm: model.dimensions.width !== undefined ? model.dimensions.width * 1000 : undefined,
        heightMm:
          model.dimensions.height !== undefined ? model.dimensions.height * 1000 : undefined,
      }
    : undefined;

  // Collect all unique GLB material names from exterior color definitions.
  // Passed to MaterialManager.init() so it knows which materials to manage.
  // Each color references one or more materials (primary + optional secondary zone).
  const materialTargets =
    resolved.features.exteriorColors && resolved.colors.length > 0
      ? [
          ...new Set(
            resolved.colors.flatMap((c) => [
              ...c.primary.materials,
              ...(c.secondary?.materials ?? []),
            ]),
          ),
        ]
      : undefined;

  // Map external rim GLBs to EngineConfig.objects for eager preloading.
  // Only sets with a `url` (non-OEM) are registered as loadable objects;
  // base-node sets (nodeName-based OEM) go into baseNodes below.
  const rimObjects: LoadAssetConfig[] =
    resolved.features.rimSelector && resolved.model.rims
      ? resolved.model.rims.sets
          .filter((s): s is typeof s & { url: string } => s.url !== undefined)
          .map((s) => ({
            id: s.id,
            url: s.url,
            group: resolved.model.rims!.group ?? 'wheels',
            phase: (s.phase as LoadAssetConfig['phase']) ?? 'lazy',
            parentRelative: true,
          }))
      : [];

  // Map rim sets with nodeName to EngineConfig.baseNodes so the engine can
  // register and wire them BEFORE setModelScene runs (inside onMounted).
  const rimBaseNodes: BaseNodeConfig[] =
    resolved.features.rimSelector && resolved.model.rims
      ? resolved.model.rims.sets
          .filter((s): s is typeof s & { nodeName: string } => s.nodeName !== undefined)
          .map((s) => ({
            id: s.id,
            nodeName: s.nodeName,
            group: resolved.model.rims!.group ?? 'wheels',
          }))
      : [];

  // Collect unique material names from interiorVariants for atlas track registration.
  // These materials will be cloned (not preset-swapped) so textures can be hot-swapped.
  const atlasMaterials =
    resolved.model.interiorVariants && resolved.model.interiorVariants.length > 0
      ? [
          ...new Set(
            resolved.model.interiorVariants.flatMap((v) =>
              v.materials.flatMap((m) => (Array.isArray(m.target) ? m.target : [m.target])),
            ),
          ),
        ]
      : undefined;

  // Map wheelSteering config → EngineConfig.wheel so WheelManager is
  // initialised with the correct steering nodes on model mount.
  // rotationNodes is empty because wheel spin is not used in brand-audi.
  const wheel = model.wheelSteering
    ? {
        rotationNodes: [],
        steeringNodes: model.wheelSteering.steeringNodes,
        steeringDurationMs: model.wheelSteering.steeringDurationMs,
      }
    : undefined;

  return {
    container,
    modelUrl: glbUrl,
    camera,
    panoFov: model.camera?.panoFov,
    dimensions,
    materialTargets,
    atlasMaterials,
    glassMaterialTargets: model.glass?.materials,
    glassType: model.glass?.type ?? 'toned-mid',
    lightSignature: model.lightSignature,
    viewerSettings:
      (resolved.brand.defaults.viewerSettings as
        | import('./types').ViewerSettingsPresetId
        | import('./types').ViewerSettingsConfig
        | undefined) ?? 'cinematic',
    environment: (resolved.model.scenes?.[resolved.model.defaultScene ?? '']?.preset ??
      resolved.model.defaultEnvironment ??
      resolved.brand.defaults.defaultEnvironment) as EngineConfig['environment'],
    autoRotate:
      resolved.brand.defaults.autoRotate !== undefined
        ? typeof resolved.brand.defaults.autoRotate === 'object'
          ? (() => {
              const ar = resolved.brand.defaults.autoRotate as Record<string, unknown>;
              return {
                ...ar,
                // Map the friendly `delay` alias → `resumeDelay` that the engine expects.
                resumeDelay:
                  (ar.resumeDelay as number | undefined) ?? (ar.delay as number | undefined),
              } as EngineConfig['autoRotate'];
            })()
          : { enabled: resolved.brand.defaults.autoRotate as boolean }
        : undefined,
    objects: rimObjects.length > 0 ? rimObjects : undefined,
    baseNodes: rimBaseNodes.length > 0 ? rimBaseNodes : undefined,
    wheel,
    ...overrides,
  };
}

/**
 * Apply a named scene config to a running engine instance.
 *
 * Calls `setPreset()` first (orchestrates env + IBL + sun + shadow),
 * then applies any per-scene overrides for IBL, sunlight and shadow.
 *
 * @param engine - Running engine instance (from engineRef.current)
 * @param scene  - Scene definition from model config (e.g. config.model.scenes['day'])
 */
export function applyScene(engine: Engine, scene: ModelSceneConfig): void {
  engine.setPreset(scene.preset);
  // Apply dome tint override if defined (studio env only, but setEnvironment
  // is a no-op on the tint if the env doesn't support it).
  if (scene.domeTint) {
    engine.setEnvironment(scene.preset, { domeTint: scene.domeTint });
  }
  if (
    scene.ibl ||
    scene.iblIntensity !== undefined ||
    scene.backgroundBlur !== undefined ||
    scene.envRotationY !== undefined ||
    scene.backgroundIntensity !== undefined
  ) {
    engine.setIBL({
      ...(scene.ibl ? { hdrFile: scene.ibl } : {}),
      ...(scene.iblIntensity !== undefined ? { intensity: scene.iblIntensity } : {}),
      ...(scene.backgroundBlur !== undefined ? { backgroundBlur: scene.backgroundBlur } : {}),
      ...(scene.envRotationY !== undefined
        ? { envRotationY: scene.envRotationY * (Math.PI / 180) }
        : {}),
      ...(scene.backgroundIntensity !== undefined
        ? { backgroundIntensity: scene.backgroundIntensity }
        : {}),
    });
  }
  if (scene.exposure !== undefined) {
    engine.setViewerSettings({ exposure: scene.exposure });
  }
  if (scene.sunlight) engine.setSunlight(scene.sunlight);
  if (scene.shadow) engine.setShadow(scene.shadow as Parameters<Engine['setShadow']>[0]);
}
